import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import SDRLayout from "../../components/sdr/SDRLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Clock, Loader2 } from "lucide-react";

const SDRHours = () => {
  const { API, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: new Date().toISOString().split("T")[0], hours: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchHours = async () => {
    try {
      const r = await fetch(`${API}/sdr/hours`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setEntries(await r.json());
    } catch (err) { console.error("Failed to load SDR hours:", err);
      console.error("Request failed:", err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchHours(); }, [API, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.hours || parseFloat(form.hours) <= 0) { toast.error("Enter valid hours"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/sdr/hours`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ date: form.date, hours: parseFloat(form.hours), notes: form.notes }),
      });
      if (r.ok) { toast.success("Hours logged"); setForm({ date: new Date().toISOString().split("T")[0], hours: "", notes: "" }); fetchHours(); }
      else { const d = await r.json(); toast.error(d.detail || "Failed"); }
    } catch { toast.error("Network error"); }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API}/sdr/hours/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success("Deleted"); fetchHours(); }
    } catch { toast.error("Failed"); }
  };

  const totalHours = entries.reduce((s, e) => s + (e.hours || 0), 0);

  return (
    <SDRLayout>
      <div className="space-y-6" data-testid="sdr-hours">
        <h1 className="text-2xl font-bold text-[#1A2636]">Hours Log</h1>
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="border-0 shadow-sm lg:col-span-1">
            <CardContent className="p-5">
              <h2 className="font-semibold text-[#1A2636] mb-4">Log Hours</h2>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="hours-date" /></div>
                <div><Label className="text-xs">Hours</Label><Input type="number" step="0.25" min="0" max="24" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} placeholder="e.g. 4.5" data-testid="hours-amount" /></div>
                <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="What did you work on?" rows={3} data-testid="hours-notes" /></div>
                <Button type="submit" disabled={submitting} className="w-full bg-sky-600 hover:bg-sky-700 gap-2" data-testid="hours-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Log Hours</>}
                </Button>
              </form>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-[#1A2636]">Recent Entries</h2>
                <span className="text-sm text-gray-500">Total: <span className="font-bold text-[#1A2636]">{totalHours.toFixed(1)}h</span></span>
              </div>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
              ) : entries.length ? (
                <div className="space-y-2">
                  {entries.map((entry) => (
                    <div key={entry.entry_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#1A2636]">{new Date(entry.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                          <span className="text-sm font-bold text-sky-600">{entry.hours}h</span>
                        </div>
                        {entry.notes && <p className="text-xs text-gray-500 mt-0.5">{entry.notes}</p>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.entry_id)} className="text-red-400 hover:text-red-600 h-8 w-8 p-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm"><Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />No hours logged yet</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </SDRLayout>
  );
};

export default SDRHours;
